/**
 * Keyboard shortcuts: Enter answer, Esc decline, Ctrl+Shift+M mute, Ctrl+Shift+H hold.
 * Enter/Esc act only while a line is ringing; Ctrl+Shift+* act on the first line up.
 */
import type { LineView } from '../sip/lines.js';

export interface ShortcutActions {
  answer(lineId: string): void;
  decline(lineId: string): void;
  mute(lineId: string, on: boolean): void;
  hold(lineId: string, on: boolean): void;
}

export type ShortcutResult = 'answer' | 'decline' | 'mute' | 'hold' | null;

export function handleShortcut(ev: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'shiftKey' | 'metaKey' | 'altKey'>, lines: readonly LineView[], a: ShortcutActions): ShortcutResult {
  const ringing = lines.find((l) => l.state === 'ringing' && !l.declining);
  const up = lines.find((l) => l.state === 'up');
  const mod = (ev.ctrlKey || ev.metaKey) && ev.shiftKey && !ev.altKey;
  if (mod && (ev.key === 'M' || ev.key === 'm') && up) {
    a.mute(up.id, !up.muted);
    return 'mute';
  }
  if (mod && (ev.key === 'H' || ev.key === 'h') && up) {
    a.hold(up.id, !up.held);
    return 'hold';
  }
  if (ev.key === 'Enter' && ringing && !ev.ctrlKey && !ev.metaKey) {
    a.answer(ringing.id);
    return 'answer';
  }
  if (ev.key === 'Escape' && ringing) {
    a.decline(ringing.id);
    return 'decline';
  }
  return null;
}

export function bindShortcuts(doc: Document, lines: () => readonly LineView[], a: ShortcutActions): () => void {
  const onKey = (ev: KeyboardEvent): void => {
    const target = ev.target as HTMLElement | null;
    const inField = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT');
    // Enter inside the dialler input dials; everything else is global
    if (inField && ev.key === 'Enter' && !lines().some((l) => l.state === 'ringing')) return;
    const r = handleShortcut(ev, lines(), a);
    if (r) ev.preventDefault();
  };
  doc.addEventListener('keydown', onKey);
  return () => doc.removeEventListener('keydown', onKey);
}
